<template>
  <div class="stock-report">
    <mainmenu selected="report_last_stock_update"></mainmenu>

    <div class="container-fluid stock-report-shell">
      <section class="report-header">
        <div>
          <h1>{{$t('report_last_stock_update.page_title')}}</h1>
          <p>{{$t('report_last_stock_update.subtitle')}}</p>
        </div>
        <div class="header-actions">
          <el-button size="small" icon="el-icon-full-screen" @click="toggle_all_countries">
            {{allCountriesExpanded ? $t('report_last_stock_update.collapse_all') : $t('report_last_stock_update.expand_all')}}
          </el-button>
          <a class="el-button el-button--success el-button--small" :href="BaseUrl +'/api/index.php/Report_last_stock_update/export_excel'">
            <i class="el-icon-download"></i>
            {{$t('report_last_stock_update.export_to_excel')}}
          </a>
        </div>
      </section>

      <section class="summary-grid">
        <div class="summary-tile">
          <strong>{{summary.Countries}}</strong>
          <span>{{$t('report_last_stock_update.countries')}}</span>
        </div>
        <div class="summary-tile">
          <strong>{{summary.Factories}}</strong>
          <span>{{$t('report_last_stock_update.factories')}}</span>
        </div>
        <div class="summary-tile">
          <strong>{{formatNumber(summary.ActiveOffers)}}</strong>
          <span>{{$t('report_last_stock_update.active_offers')}}</span>
        </div>
        <div class="summary-tile attention">
          <strong>{{summary.NeedsAttention}}</strong>
          <span>{{$t('report_last_stock_update.needs_attention')}}</span>
        </div>
      </section>

      <section class="country-report-card" v-loading="tableLoadingVisible">
        <div class="report-card-title">
          <div>
            <span>{{$t('report_last_stock_update.factory_overview')}}</span>
            <small>{{$t('report_last_stock_update.grouped_by_country')}}</small>
          </div>
          <span class="total-pill">{{$t('report_last_stock_update.total_factories')}} {{summary.Factories}}</span>
        </div>

        <el-collapse v-if="countryGroups.length>0" v-model="openCountries" class="country-collapse">
          <el-collapse-item
            v-for="country in countryGroups"
            :key="country.Key"
            :name="country.Key">
            <template slot="title">
              <div class="country-title">
                <div class="country-name">
                  <strong>{{country.Label}}</strong>
                  <small>{{country.Rows.length}} {{$t('report_last_stock_update.factories_short')}}</small>
                </div>
                <div class="country-metrics">
                  <span>{{formatNumber(country.ActiveOffers)}} {{$t('report_last_stock_update.offers_short')}}</span>
                  <span>{{formatNumber(country.ActiveQuantity)}} {{$t('report_last_stock_update.pieces_short')}}</span>
                  <span v-if="country.NeedsAttention>0" class="attention-count">
                    {{country.NeedsAttention}} {{$t('report_last_stock_update.needs_attention_short')}}
                  </span>
                </div>
              </div>
            </template>

            <el-table
              class="factory-table"
              :data="country.Rows"
              stripe
              :empty-text="$t('report_last_stock_update.no_factory_data')">
              <el-table-column :label="$t('report_last_stock_update.factory')" width="190">
                <template slot-scope="scope">
                  <div class="factory-cell">
                    <strong>{{scope.row.FactoryShortCode}}</strong>
                    <el-tag size="mini" :type="statusType(scope.row.StockStatus)" effect="plain">
                      {{statusLabel(scope.row.StockStatus)}}
                    </el-tag>
                  </div>
                </template>
              </el-table-column>
              <el-table-column :label="$t('report_last_stock_update.stock_actors')" min-width="290">
                <template slot-scope="scope">
                  <div v-if="actorList(scope.row).length" class="actor-tags">
                    <el-tag
                      v-for="actor in actorList(scope.row)"
                      :key="scope.row.FactoryId + actor"
                      size="mini"
                      type="info"
                      effect="plain">
                      {{actor}}
                    </el-tag>
                  </div>
                  <span v-else class="muted-text">{{$t('report_last_stock_update.no_stock_actors')}}</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('report_last_stock_update.active_offers')" width="120" align="right">
                <template slot-scope="scope">{{formatNumber(scope.row.ActiveOffers)}}</template>
              </el-table-column>
              <el-table-column :label="$t('report_last_stock_update.active_articles')" width="125" align="right">
                <template slot-scope="scope">{{formatNumber(scope.row.ActiveArticles)}}</template>
              </el-table-column>
              <el-table-column :label="$t('report_last_stock_update.active_quantity')" width="130" align="right">
                <template slot-scope="scope">{{formatNumber(scope.row.ActiveQuantity)}}</template>
              </el-table-column>
              <el-table-column :label="$t('report_last_stock_update.estimated_value')" width="135" align="right">
                <template slot-scope="scope">{{formatMoney(scope.row.ActiveValue)}} &euro;</template>
              </el-table-column>
              <el-table-column :label="$t('report_last_stock_update.last_update')" width="145">
                <template slot-scope="scope">{{formatDate(scope.row.LastStockUpdate)}}</template>
              </el-table-column>
              <el-table-column :label="$t('report_last_stock_update.days_since_update')" width="145" align="right">
                <template slot-scope="scope">
                  <span v-if="scope.row.Days!='-1'">{{scope.row.Days}} {{$t('report_last_stock_update.days')}}</span>
                  <span v-else class="muted-text">-</span>
                </template>
              </el-table-column>
            </el-table>
          </el-collapse-item>
        </el-collapse>

        <div v-else class="empty-state">
          {{$t('report_last_stock_update.no_factory_data')}}
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import MainMenu from "./../../widgets/MainMenu";
import settings from "./../../backend/LocalSettings";
import { countryKey, countryLabel } from "./../../utils/countries";

export default {
  name: "report_last_stock_update",
  components: {
    "mainmenu": MainMenu
  },
  data () {
    return {
      BaseUrl: settings.base_url,
      tableLoadingVisible: false,
      Report: [],
      openCountries: []
    }
  },
  computed: {
    countryGroups: function() {
      var groups = {};
      (this.Report || []).forEach((row) => {
        var key = countryKey(row.Country) || row.Country || "unknown";
        if(!groups[key]) {
          groups[key] = {
            Key: key,
            Label: this.country_label(row.Country),
            Rows: [],
            ActiveOffers: 0,
            ActiveQuantity: 0,
            NeedsAttention: 0
          };
        }

        groups[key].Rows.push(row);
        groups[key].ActiveOffers += Number(row.ActiveOffers || 0);
        groups[key].ActiveQuantity += Number(row.ActiveQuantity || 0);
        if(row.StockStatus !== "current") groups[key].NeedsAttention += 1;
      });

      return Object.values(groups).sort((a, b) => a.Label.localeCompare(b.Label));
    },
    summary: function() {
      var rows = this.Report || [];
      return {
        Countries: this.countryGroups.length,
        Factories: rows.length,
        ActiveOffers: rows.reduce((sum, row) => sum + Number(row.ActiveOffers || 0), 0),
        NeedsAttention: rows.filter((row) => row.StockStatus !== "current").length
      };
    },
    allCountriesExpanded: function() {
      return this.countryGroups.length > 0 && this.openCountries.length === this.countryGroups.length;
    }
  },
  methods: {
    country_label: function(country) {
      return countryLabel(this, country);
    },
    syncDefaultOpenCountries: function() {
      if(this.openCountries.length > 0) return;
      var austria = this.countryGroups.find((country) => country.Label === "Austria" || country.Label === "Österreich");
      this.openCountries = austria ? [austria.Key] : this.countryGroups.slice(0, 1).map((country) => country.Key);
    },
    toggle_all_countries: function() {
      this.openCountries = this.allCountriesExpanded ? [] : this.countryGroups.map((country) => country.Key);
    },
    actorList: function(row) {
      if(!row.ActiveUsers) return [];
      return String(row.ActiveUsers)
        .split(",")
        .map((name) => name.trim())
        .filter((name) => name.length > 0);
    },
    statusLabel: function(status) {
      if(status === "current") return this.$t('report_last_stock_update.status_current');
      if(status === "stale") return this.$t('report_last_stock_update.status_stale');
      return this.$t('report_last_stock_update.status_never');
    },
    statusType: function(status) {
      if(status === "current") return "success";
      if(status === "stale") return "warning";
      return "danger";
    },
    formatDate: function(value) {
      if(!value || value === "0000-00-00 00:00:00") return this.$t('report_last_stock_update.never');
      var parts = String(value).split(/[- :]/);
      if(parts.length < 3) return value;
      var pad = function(part) { return String(part).padStart(2, "0"); };
      return pad(parts[2]) + "." + pad(parts[1]) + "." + parts[0];
    },
    formatNumber: function(value) {
      var number = Number(value || 0);
      return new Intl.NumberFormat().format(number);
    },
    formatMoney: function(value) {
      var number = Number(value || 0);
      return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number);
    },
    refresh_info: function() {
      this.tableLoadingVisible = true;
      this.$http
        .post('report_last_stock_update/refresh_info', {})
        .then(function (response) {
          this.tableLoadingVisible = false;
          if(settings.verify_response(response, this.$rights)) {
            this.Report = response.body.Report || [];
            this.$nextTick(this.syncDefaultOpenCountries);
          } else {
            this.$router.push("/");
          }
        })
        .catch(function() {
          this.tableLoadingVisible = false;
          this.$message.error(this.$t('report_last_stock_update.load_failed'));
        });
    }
  },
  mounted: function() {
    this.refresh_info();
  }
};
</script>

<style lang="less" scoped>
.stock-report-shell {
  padding: 14px 10px 28px;
}

.report-header,
.summary-tile,
.country-report-card {
  border: 1px solid rgba(26, 88, 111, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 12px 26px rgba(25, 80, 99, 0.13);
}

.report-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;
  padding: 18px;

  h1 {
    margin: 0;
    color: #152b38;
    font-size: 31px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 36px;
  }

  p {
    margin: 3px 0 0;
    color: rgba(21, 43, 56, 0.72);
    font-size: 13px;
    font-weight: 600;
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.summary-tile {
  padding: 14px 16px;
  min-height: 76px;

  strong {
    display: block;
    color: #173746;
    font-size: 26px;
    line-height: 30px;
  }

  span {
    color: #607685;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  &.attention {
    border-color: rgba(213, 132, 42, 0.28);
    background: #fffaf3;

    strong {
      color: #9a5b18;
    }
  }
}

.country-report-card {
  overflow: hidden;
}

.report-card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 13px 18px;
  border-bottom: 1px solid #e6edf2;
  background: #fbfdfe;
  color: #223947;
  font-size: 15px;
  font-weight: 800;

  small {
    display: block;
    margin-top: 2px;
    color: #6b7c87;
    font-size: 12px;
    font-weight: 700;
  }
}

.total-pill {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 4px;
  background: #eef6f9;
  color: #2b6275;
  font-size: 12px;
  font-weight: 800;
}

.country-collapse {
  border: 0;
}

.country-collapse /deep/ .el-collapse-item__header {
  height: auto;
  min-height: 58px;
  padding: 0 18px;
  border-bottom-color: #e5edf2;
}

.country-collapse /deep/ .el-collapse-item__content {
  padding: 0;
}

.country-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 18px;
  padding-right: 16px;
}

.country-name {
  strong {
    color: #173746;
    font-size: 17px;
  }

  small {
    display: block;
    margin-top: 2px;
    color: #607685;
    font-size: 12px;
    font-weight: 700;
  }
}

.country-metrics {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 7px;

  span {
    min-height: 24px;
    padding: 2px 8px;
    border-radius: 4px;
    background: #eef6f9;
    color: #2f6477;
    font-size: 12px;
    font-weight: 800;
    line-height: 20px;
  }

  .attention-count {
    background: #fff3e4;
    color: #9a5b18;
  }
}

.factory-table /deep/ th {
  padding: 9px 0;
  background: #f4f8fa;
  color: #526875;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
}

.factory-table /deep/ td {
  padding: 8px 0;
  border-bottom-color: #e7edf2;
  color: #405461;
}

.factory-cell {
  display: flex;
  align-items: center;
  gap: 8px;

  strong {
    min-width: 62px;
    color: #263f4d;
  }
}

.actor-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.muted-text {
  color: #87949d;
  font-size: 12px;
  font-weight: 700;
}

.empty-state {
  padding: 36px;
  color: #778792;
  text-align: center;
  font-weight: 700;
}

@media (max-width: 900px) {
  .report-header,
  .country-title {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions,
  .country-metrics {
    justify-content: flex-start;
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(150px, 1fr));
  }
}
</style>
